/**
 * offline-documents.ts
 * "Mis documentos" with no signal (operator-offline-documents). The last list
 * the server answered and every file the worker opened stay on the phone
 * (document-store.ts), per user (`${tenantId}:${sub}`, same owner key as the
 * dictation queue), so the screen and those files open on the plateau.
 *
 *   - A file is fetched once from GET /files/:id and then always served from
 *     the phone: a fileId is one immutable upload, so the copy never goes stale.
 *   - Files are capped (count and bytes) — phones have little room — dropping
 *     the least recently opened first; a file bigger than the cap is shown but
 *     not kept.
 *   - Signing out keeps them, like downloads in a music app: the worker signs
 *     back in and they are still there (owner, 2026-11-01: "una vez que
 *     descargas un documento queda disponible no los borra al cerrar sesion").
 *     Everything is keyed by owner, so another user signing in on the same
 *     phone never sees them.
 *   - Prefetch (operator-document-prefetch): with signal, every current
 *     document is downloaded without the worker opening it — on sign-in, on
 *     start, when the connection is back and when the app returns to the
 *     foreground — so a worker who never opened "Mis documentos" can still
 *     show a permit at the gate. Current documents are never evicted to make
 *     room; files of documents no longer on the list go first. After each
 *     run the phone tells RRHH which files it holds, so the panel shows who
 *     would get stuck at the gate.
 * A storage failure (private mode, quota) never breaks the screen: it just
 * behaves as if nothing were kept.
 */
import { ref, watch } from 'vue';
import type { SelfEmployeeDocumentResponse } from '@suite/contracts-rrhh';
import { getMyDocuments, reportDocumentOfflineStatus } from './api.js';
import { fetchFileBlob } from './http.js';
import { currentClaims } from './session.js';
import { indexedDbDocumentStore, type CachedDocumentList, type DocumentStore } from './document-store.js';

export interface CacheLimits {
  readonly maxFiles: number;
  readonly maxBytes: number;
}

const DEFAULT_LIMITS: CacheLimits = { maxFiles: 60, maxBytes: 150 * 1024 * 1024 };

let store: DocumentStore = indexedDbDocumentStore;
let limits: CacheLimits = DEFAULT_LIMITS;

/** Swap the backing store and caps (tests). */
export function useDocumentStore(next: DocumentStore, nextLimits: Partial<CacheLimits> = {}): void {
  store = next;
  limits = { ...DEFAULT_LIMITS, ...nextLimits };
}

function currentOwner(): string | null {
  const claims = currentClaims.value;
  return claims?.scope === 'tenant' ? `${claims.tenantId}:${claims.sub}` : null;
}

async function quietly<T>(run: () => Promise<T>, fallback: T): Promise<T> {
  try {
    return await run();
  } catch {
    return fallback;
  }
}

/** The last list the server answered for the signed-in user, if any. */
export async function cachedDocuments(): Promise<CachedDocumentList | null> {
  const owner = currentOwner();
  if (!owner) return null;
  return quietly(async () => (await store.getList(owner)) ?? null, null);
}

/** GET /rrhh/self/documents, kept on the phone. Throws the ApiError untouched. */
export async function fetchMyDocuments(): Promise<CachedDocumentList> {
  const { items } = await getMyDocuments();
  const row: CachedDocumentList = { owner: currentOwner() ?? '', items, savedAt: new Date().toISOString() };
  if (row.owner) await quietly(() => store.putList(row), undefined);
  return row;
}

/** The fileIds this user can open with no signal. */
export async function cachedFileIds(): Promise<Set<string>> {
  const owner = currentOwner();
  if (!owner) return new Set();
  const rows = await quietly(() => store.files(), []);
  return new Set(rows.filter((r) => r.owner === owner).map((r) => r.fileId));
}

/**
 * Drop files until `incoming` more bytes fit: files outside `keep` (the
 * owner's current documents) first, least recently opened first. Returns
 * false when it only fits by evicting a file in `keep` — the caller then
 * does not store the new one.
 */
async function makeRoom(incoming: number, keep: ReadonlySet<string> = new Set()): Promise<boolean> {
  const rows = (await store.files()).sort(
    (a, b) => Number(keep.has(a.key)) - Number(keep.has(b.key)) || a.lastOpenedAt.localeCompare(b.lastOpenedAt),
  );
  let count = rows.length;
  let bytes = rows.reduce((sum, r) => sum + r.size, 0);
  for (const row of rows) {
    if (count < limits.maxFiles && bytes + incoming <= limits.maxBytes) break;
    if (keep.has(row.key)) return false;
    await store.removeFile(row.key);
    count -= 1;
    bytes -= row.size;
  }
  return count < limits.maxFiles && bytes + incoming <= limits.maxBytes;
}

/**
 * The document's file: from the phone when it was opened before (no network
 * at all), otherwise downloaded and kept. Throws an ApiError (status 0 with no
 * signal) when it was never opened and cannot be downloaded.
 */
export async function openDocumentFile(fileId: string): Promise<Blob> {
  const owner = currentOwner();
  const key = `${owner}:${fileId}`;
  if (owner) {
    const kept = await quietly(() => store.getFile(key), undefined);
    if (kept) {
      await quietly(() => store.putFile({ ...kept, lastOpenedAt: new Date().toISOString() }), undefined);
      return kept.blob;
    }
  }

  const blob = await fetchFileBlob(fileId, 'No se pudo abrir el documento.');
  if (owner && blob.size <= limits.maxBytes) {
    await quietly(async () => {
      if (!(await makeRoom(blob.size, await currentKeys(owner)))) return;
      await store.putFile({ key, owner, fileId, blob, size: blob.size, lastOpenedAt: new Date().toISOString() });
    }, undefined);
  }
  return blob;
}

/** Keys of the owner's current documents in the kept list: never evicted. */
async function currentKeys(owner: string): Promise<Set<string>> {
  const list = await store.getList(owner);
  const today = localToday();
  return new Set((list?.items ?? []).filter((d) => isCurrent(d, today)).map((d) => `${owner}:${d.fileId}`));
}

/** A document whose file the phone should hold: it has one and is not expired. */
export function isCurrent(doc: SelfEmployeeDocumentResponse, today: string = localToday()): boolean {
  return doc.fileId !== null && (doc.expiresAt === null || doc.expiresAt >= today);
}

function localToday(): string {
  const now = new Date();
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

export interface OfflineReadiness {
  /** Current documents with a file. */
  readonly total: number;
  /** Their fileIds already on the phone. */
  readonly readyFileIds: readonly string[];
  /** When it was last checked against the server (ISO). */
  readonly checkedAt: string;
}

/** Last prefetch outcome for the signed-in user (banner in Mis documentos). */
export const offlineReadiness = ref<OfflineReadiness | null>(null);

/**
 * Download every current document not yet on the phone. Throws the ApiError
 * of the list call (no signal, 401); a single file failing is skipped and
 * retried on the next run.
 */
export async function prefetchMyDocuments(
  list?: readonly SelfEmployeeDocumentResponse[],
): Promise<OfflineReadiness | null> {
  const owner = currentOwner();
  if (!owner) return null;
  // The screen passes the list it just loaded, so it is not asked twice.
  const items = list ?? (await fetchMyDocuments()).items;
  const today = localToday();
  const wanted = [...new Set(items.filter((d) => isCurrent(d, today)).map((d) => d.fileId as string))];
  const keep = await quietly(() => currentKeys(owner), new Set<string>());

  const kept = await quietly(() => store.files(), []);
  const listed = new Set(items.flatMap((d) => (d.fileId ? [`${owner}:${d.fileId}`] : [])));
  // A replaced or removed document's old file only takes room.
  for (const row of kept) {
    if (row.owner === owner && !listed.has(row.key)) await quietly(() => store.removeFile(row.key), undefined);
  }

  const have = new Set(kept.filter((r) => r.owner === owner).map((r) => r.fileId));
  for (const fileId of wanted) {
    if (have.has(fileId)) continue;
    try {
      const blob = await fetchFileBlob(fileId, 'No se pudo descargar el documento.');
      if (blob.size > limits.maxBytes) continue;
      await quietly(async () => {
        if (!(await makeRoom(blob.size, keep))) return;
        await store.putFile({ key: `${owner}:${fileId}`, owner, fileId, blob, size: blob.size, lastOpenedAt: new Date(0).toISOString() });
      }, undefined);
    } catch {
      // Skipped: the next run tries again.
    }
  }

  const ready = await cachedFileIds();
  const result: OfflineReadiness = {
    total: wanted.length,
    readyFileIds: wanted.filter((id) => ready.has(id)),
    checkedAt: new Date().toISOString(),
  };
  // Another user signed in while this ran: their session must not carry this
  // user's banner or report.
  if (currentOwner() !== owner) return result;
  offlineReadiness.value = result;
  // Best effort: RRHH's panel just keeps the previous report until the next run.
  await quietly(() => reportDocumentOfflineStatus({ readyFileIds: [...result.readyFileIds], checkedAt: result.checkedAt }), undefined);
  return result;
}

const PREFETCH_EVERY_MS = 15 * 60_000;
let lastPrefetch: { owner: string; at: number } | null = null;
let running: { owner: string; done: Promise<unknown> } | null = null;

/**
 * Prefetch unless it already ran for this user in the last 15 minutes
 * (`force` skips that: sign-in). One run at a time: a call while this user's
 * run is going just waits for it; one for a user who signed in meanwhile waits
 * for the previous user's run to end, then runs. Never throws — no signal just
 * waits for the next trigger.
 */
export async function prefetchSoon(force = false, list?: readonly SelfEmployeeDocumentResponse[]): Promise<void> {
  const owner = currentOwner();
  if (!owner) return;
  while (running) {
    const current = running;
    await current.done;
    if (current.owner === owner) return;
  }
  if (currentOwner() !== owner) return;
  if (!force && lastPrefetch?.owner === owner && Date.now() - lastPrefetch.at < PREFETCH_EVERY_MS) return;
  const done = prefetchMyDocuments(list)
    .then(() => {
      lastPrefetch = { owner, at: Date.now() };
    })
    .catch(() => undefined);
  running = { owner, done };
  try {
    await done;
  } finally {
    if (running?.done === done) running = null;
  }
}

/**
 * Keep the worker's documents on the phone with no action from them: on
 * start, on sign-in (a new owner), when the connection is back and when the
 * app returns to the foreground. Also asks the browser to keep this site's
 * storage instead of clearing it when the phone runs low on space.
 */
export function startDocumentPrefetch(): void {
  void navigator.storage?.persist?.().catch(() => undefined);
  watch(
    () => currentOwner(),
    (owner, previous) => {
      if (owner && owner !== previous) void prefetchSoon(true);
    },
    { immediate: true },
  );
  window.addEventListener('online', () => void prefetchSoon(true));
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') void prefetchSoon();
  });
}

/**
 * Sign-out: forget this session's in-memory state only. The kept lists and
 * files stay on the phone, under their owner.
 */
export function resetDocumentPrefetch(): void {
  offlineReadiness.value = null;
  lastPrefetch = null;
}
